import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { onConnectionState, subscribe, unsubscribe } from '@/lib/realtime';
import type { DrawSnapshot } from '@/lib/types';

interface DrawTickedEvent {
    game_id: number;
    sequence: number;
    number: number;
    drawn_at: string | null;
    numbers_drawn_count: number;
}

export interface GameLiveState {
    snapshot: DrawSnapshot | null;
    error: string | null;
    socketState: string;
}

/**
 * Live draw sync with WebSocket + REST backstop.
 *
 * Money-game rule: never rely on WS alone. While disconnected (or always as
 * a quiet backstop), poll `/snapshot` so missed balls cannot strand players.
 */
export function useGameLive(gameId: number | null): GameLiveState {
    const [snapshot, setSnapshot] = useState<DrawSnapshot | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [socketState, setSocketState] = useState<string>('initialized');
    const snapshotRef = useRef<DrawSnapshot | null>(null);
    const socketStateRef = useRef<string>('initialized');

    useEffect(() => {
        snapshotRef.current = snapshot;
    }, [snapshot]);

    useEffect(() => {
        socketStateRef.current = socketState;
    }, [socketState]);

    useEffect(() => {
        if (gameId === null) {
            return;
        }

        let cancelled = false;

        const pullSnapshot = async (): Promise<void> => {
            try {
                const res = await api.get<{ data: DrawSnapshot }>(`/games/${gameId}/snapshot`);
                if (!cancelled) {
                    setSnapshot(res.data.data);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Failed to load game snapshot.');
                }
            }
        };

        void pullSnapshot();

        const channelName = `games.${gameId}`;
        const channel = subscribe(channelName);

        const onTick = (payload: DrawTickedEvent): void => {
            setSnapshot((prev) => {
                if (!prev || prev.game_id !== payload.game_id) {
                    return prev;
                }
                if (payload.sequence <= prev.last_sequence) {
                    return prev;
                }
                // Gap — do not invent balls; wait for authoritative snapshot.
                if (payload.sequence > prev.last_sequence + 1) {
                    void pullSnapshot();
                    return prev;
                }
                return {
                    ...prev,
                    numbers: [...prev.numbers, payload.number],
                    numbers_drawn_count: payload.numbers_drawn_count,
                    last_sequence: payload.sequence,
                };
            });
        };

        channel.bind('draw.ticked', onTick);

        const offConn = onConnectionState((s) => {
            setSocketState(s.current);
            if (s.previous !== 'connected' && s.current === 'connected') {
                void pullSnapshot();
            }
        });

        const onVisible = (): void => {
            if (document.visibilityState === 'visible') {
                void pullSnapshot();
            }
        };
        document.addEventListener('visibilitychange', onVisible);

        // Always poll as a backstop (faster when WS is down).
        const pollMs = () => (socketStateRef.current === 'connected' ? 4000 : 1500);
        let timer: number | undefined;
        const armPoll = (): void => {
            timer = window.setTimeout(() => {
                void pullSnapshot().finally(() => {
                    if (!cancelled) armPoll();
                });
            }, pollMs());
        };
        armPoll();

        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
            channel.unbind('draw.ticked', onTick);
            unsubscribe(channelName);
            offConn();
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [gameId]);

    return { snapshot, error, socketState };
}
